import styled from 'styled-components';
import { Dialog } from 'components/shared/index';
import { useTranslation } from 'react-i18next';
import {
  AskedDutyResponse,
  AskedDutyRoundResponse,
  AskedDutyRoundSettingsResponse
} from 'generated/index';
import dayjs from 'dayjs';
import { Box, Chip, List, ListItem, ListItemText, Paper } from '@mui/material';
import { useState } from 'react';
import {
  BLUE,
  BLUE_DARK,
  BLUE_SECONDARY,
  BLUE_TERTIARY,
  GRAY_LIGHT_SECONDARY,
  GRAY_SECONDARY,
  PRIMARY,
  PRIMARY_LIGHT,
  RED,
  RED_LIGHT
} from 'constants/colors';
import { formatDateTime } from 'utils/date';
import { GridColDef } from '@mui/x-data-grid-pro';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { Icon } from '@sdb/planning-ui';

type Props = {
  open: boolean;
  askedDuty: AskedDutyResponse;
  handleClose: () => void;
};

const CustomDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '80%', // here you set custom width
    maxWidth: 'none',
    height: '80%'
  }
});

export default function DutyRoundsDialog({
  open,
  handleClose,
  askedDuty
}: Props) {
  const { t } = useTranslation('common');
  const [uncollapsedRounds, setUncollapsedRounds] = useState<number[]>([]);

  const getEmployeeTypesString = (round: AskedDutyRoundResponse) => {
    const keys: Array<keyof AskedDutyRoundSettingsResponse> = [
      'includeOwn',
      'includeShared',
      'includePreference1',
      'includePreference2',
      'includeFlexWithPreference1',
      'includeFlexWithPreference2',
      'includeFlex',
      'includeFunctionPreference1',
      'includeFunctionPreference2',
      'includeDeployableAsFlexWithPreference1',
      'includeDeployableAsFlexWithPreference2',
      'includeDeployableAsFlex'
    ];

    const includedItems: string[] = keys
      .filter((key) => round.settings[key])
      .map((key) => t(`askedEmployeeTypes.${key}`));

    return includedItems.join(', ');
  };

  const roundStatusColor = {
    PLANNED: {
      label: t('askedDutyRoundStatus.planned'),
      style: { backgroundColor: BLUE_TERTIARY, color: BLUE }
    },
    PROCESSED: {
      label: t('askedDutyRoundStatus.processed'),
      style: { backgroundColor: PRIMARY_LIGHT, color: PRIMARY }
    },
    CANCELLED: {
      label: t('askedDutyRoundStatus.cancelled'),
      style: { backgroundColor: GRAY_LIGHT_SECONDARY, color: GRAY_SECONDARY }
    }
  };

  const askedEmployeeStatusColor = {
    ASKED: {
      label: t('askedEmployeeStatus.asked'),
      style: { backgroundColor: BLUE_TERTIARY, color: BLUE }
    },
    NOTIFIED: {
      label: t('askedEmployeeStatus.notified'),
      style: { backgroundColor: BLUE_SECONDARY, color: BLUE_DARK }
    },
    RESPONDED_AVAILABLE: {
      label: t('askedEmployeeStatus.available'),
      style: { backgroundColor: PRIMARY_LIGHT, color: PRIMARY }
    },
    RESPONDED_NOT_AVAILABLE: {
      label: t('askedEmployeeStatus.notAvailable'),
      style: { backgroundColor: RED_LIGHT, color: RED }
    },
    CANCELLED: {
      label: t('askedEmployeeStatus.cancelled'),
      style: { backgroundColor: GRAY_LIGHT_SECONDARY, color: GRAY_SECONDARY }
    }
  };

  const renderAskedEmployeeStatusColumn = (params) => {
    const value = params.value;
    const statusConfig =
      askedEmployeeStatusColor[value] || askedEmployeeStatusColor.CANCELLED;
    return (
      <Chip
        label={statusConfig.label}
        size="small"
        style={statusConfig.style}
      />
    );
  };
  const title = `${dayjs(askedDuty.duty.start).format(
    'dddd DD MMMM YYYY HH:mm'
  )}-${dayjs(askedDuty.duty.end).format('HH:mm')} (${askedDuty.duty.dutyCode
    ?.dutyCode}) - ${askedDuty.duty.assignedOrganizationalUnit?.name ?? ''}`;

  return (
    <CustomDialog
      open={open}
      maxWidth="extralarge"
      handleClose={handleClose}
      title={title}
    >
      <Box display="flex" flexDirection="column" gap={2} mt={2} ml={2}>
        {askedDuty &&
          askedDuty.rounds?.map((round, i: number) => {
            const isUncollapsed = uncollapsedRounds?.includes(i);
            const roundStatusConfig =
              roundStatusColor[round.status] ?? roundStatusColor.CANCELLED;

            const columns: GridColDef[] = [
              { field: 'employee', headerName: t('employee'), flex: 1 },
              {
                field: 'canConnect',
                headerName: t('connect'),
                width: 70,
                align: 'center',
                renderCell: ({ value }) => <BooleanCell value={value} />
              },
              {
                field: 'askedAt',
                headerName: t('askedAt'),
                minWidth: 120,
                renderCell: ({ value }) => formatDateTime(value)
              },
              {
                field: 'notifiedAt',
                headerName: t('notifiedAt'),
                minWidth: 120,
                renderCell: ({ value }) => formatDateTime(value)
              },
              {
                field: 'status',
                headerName: t('status'),
                minWidth: 140,
                renderCell: (params) => renderAskedEmployeeStatusColumn(params)
              }
            ];

            const rows =
              round.askedEmployees.map((d) => ({
                id: d.employee?.entityId,
                employee: d.employee?.nameInformal,
                canConnect: d.responseActionType == 'CONNECT',
                askedAt: d.askedAt,
                notifiedAt: d.notifiedAt,
                response: d.response,
                status: d.status
              })) ?? [];
            return (
              <Paper key={i}>
                <List>
                  <ListItem
                    onClick={() =>
                      setUncollapsedRounds(
                        isUncollapsed
                          ? uncollapsedRounds?.filter((m) => m !== i)
                          : [...uncollapsedRounds, i]
                      )
                    }
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      backgroundColor: isUncollapsed
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'transparent'
                    }}
                  >
                    <Box>
                      <ListItemText>
                        {t('aiMatchSettings.roundNr', { number: i + 1 })}
                      </ListItemText>
                      <Box>{getEmployeeTypesString(round)}</Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <Box
                        display="flex"
                        flexDirection="column"
                        mr={2}
                        gap={0.5}
                        alignItems="center"
                      >
                        <Box>{t('asked')}</Box>
                        <ListItemText>
                          {round.numberOfAskedEmployees}
                        </ListItemText>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        mr={2}
                        gap={0.5}
                        alignItems="center"
                      >
                        <Box>{t('notified')}</Box>
                        <ListItemText>
                          {round.numberOfNotifiedEmployees}
                        </ListItemText>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        mr={2}
                        gap={0.5}
                        alignItems="center"
                      >
                        <Box>{t('responded')}</Box>
                        <ListItemText>
                          {round.numberOfRespondedEmployees}
                        </ListItemText>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        mr={2}
                        gap={0.5}
                      >
                        <Chip
                          label={roundStatusConfig.label}
                          size="small"
                          style={roundStatusConfig.style}
                        />
                        <Box>
                          {round.processedAt
                            ? dayjs(round.processedAt).format(
                                'DD-MM-YYYY HH:mm'
                              )
                            : dayjs(round.dueAt).format('DD-MM-YYYY HH:mm')}
                        </Box>
                      </Box>
                      <Icon
                        type={isUncollapsed ? 'chevron-up' : 'chevron-down'}
                        fontSize="small"
                        color="primary"
                      />
                    </Box>
                  </ListItem>

                  {/* Content */}
                  <ListItem
                    sx={{
                      overflow: 'hidden',
                      maxHeight: isUncollapsed ? '100vh' : '0px',
                      padding: isUncollapsed ? '16px' : '0px',
                      opacity: isUncollapsed ? 1 : 0,
                      transition: 'max-height 150ms ease-out'
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <TableCRUDLayout
                        columns={columns}
                        rows={rows}
                        editable={false}
                        pagination
                        pageSize={10}
                        pageSizeOptions={[10]}
                        disableActions
                      />
                    </Box>
                  </ListItem>
                </List>
              </Paper>
            );
          })}
      </Box>
    </CustomDialog>
  );
}
